import { Injectable } from '@angular/core';
import { enumeratDevicesTypeEnum, EnvironmentEnum, MediaDevicesEnum, RecordingTypeEnum } from '@app/core/helpers/enums/enums';
import { errorCodeMsg, errorMessage } from '@app/core/constants/app-message.constant';
import { MatDialog } from '@angular/material/dialog';
import { DevicePermissionDialogComponent } from '@app/modules/session/components/device-permission-dialog/device-permission-dialog.component';
import { getVideoResoultion, isObjectValidAndNotEmpty, sleep } from '@app/core/helpers/utils';
import { RecIndexDbService } from '../index-db/rec-index-db.service';
import { CommonService } from '../common.service';
import { CB_APP_CONSTRAINTS, DEVICES } from '@app/configs/app-settings.config';
import { LoggerService } from '../logger.service';
import { IManageSessionConstraint } from '@app/core/interfaces/device.constraints';
import { environment } from '@environment/environment';
export type MediaRecorderType = any;//MediaRecorder
declare var MediaRecorder: any;


@Injectable({
    providedIn: 'root'
})
/**
 * local recording servcie
 */
export class LocalWebRtcService {

    private mediaRecorder: any;
    private localStream: MediaStream = null;
    mimeType = '';
    isPermsionPopup = false;
    videoResolution = ''

    /**
     * constructor
     */
    constructor(public dialog: MatDialog,
        private readonly logger: LoggerService,
        private readonly recIndexDbService: RecIndexDbService,
        private readonly commonService: CommonService
    ) {
        try {
            this.mimeType = this.getSupportedMimeType();
        } catch (error) { }
    }

    /**
    * get mimetype
    * @param mimetype 
    */
    get getCodecExtension() {
        try {
            return this.mimeType && this.mimeType.includes("x-matroska") ? 'mkv' : 'webm'
        } catch (error) {
            this.logger.error('getCodecExtension', error);
            return 'mkv'
        }
    }

    /**
     * start local recording
     * @param stream webrtc stream
     * @param timeslice milisec to provide rec blob
     */
    async initAndStartRecording(constraintConfig: IManageSessionConstraint,
        onError: (e: any) => void, onStop: (e: any) => void, onStart: (e: any) => void,
        onDataAvailable: (e: any) => void) {

        return new Promise(async (res: any, rej: any) => {
            try {
                let options = {
                    mimeType: this.mimeType,
                    audioBitsPerSecond: 128e3,
                    videoBitsPerSecond: this.getVideoBitRate(this.localStream)
                };
                if (!constraintConfig.isCam) {
                    this.disableVideo();
                }
                if (!constraintConfig.isMic) {
                    this.disableAudio();
                }
                try {
                    if (environment.env !== EnvironmentEnum.production && window['lsErrTest']) {
                        this.localStream = null;
                    }
                } catch (error) { }
                this.mediaRecorder = new MediaRecorder(this.localStream, options);
                this.mediaRecorder.onerror = onError;
                this.mediaRecorder.onstart = onStart;
                this.mediaRecorder.onstop = onStop;
                this.mediaRecorder.ondataavailable = onDataAvailable
                this.mediaRecorder.start();
                this.requestDataByEvent(this.mediaRecorder);
                res(true);
            } catch (error) {
                this.logger.error('Error in start recording by mediaRecoder: ', error);
                //rej(error);
                onError(error)
            }
        })
    }

    /**
     * request data of recording
     * @param ms 
     */
    async requestData(ms: number = 9e3) {
        try {
            await this.commonService.sleep(ms);
            const state = this.getStateMediaRecorder();
            if (state === MediaRecorderStateEnum.recording) {
                this.mediaRecorder.requestData();
            }
            return {
                mediaRecorderState: state
            }
        } catch (error) {
            this.handleError(LocalWebRtcErrorEnum.RequestChunks, 'requestData', error)
        }
    }

    /**
     * request data by mediarecoder instance
     * @param mediaRecorder 
     * @param ms 
     * @returns 
     */
    async requestDataByEvent(mediaRecorder: MediaRecorderType, ms: number = 2e3) {
        try {
            await sleep(ms);
            const state = mediaRecorder.state;
            if (state === MediaRecorderStateEnum.recording) {
                mediaRecorder.requestData();
            }
            return {
                mediaRecorderState: state
            }
        } catch (error) {
            this.handleError(LocalWebRtcErrorEnum.RequestChunks, 'requestData', error)
        }
    }



    /**
     * 
     * @returns get state of media recorder
     */
    getStateMediaRecorder(): MediaRecorderStateEnum {
        try {
            return this.mediaRecorder.state;
        } catch (error) { }
    }

    /**
     * stop media recorder
     */
    async stopRecording() {
        return new Promise((res: any, rej: any) => {
            try {
                const state = this.getStateMediaRecorder();
                if (state === MediaRecorderStateEnum.recording) {
                    this.mediaRecorder.stop();
                }
                res(true);
            } catch (error) {
                rej(error);
            }
        });
    }

    /**
     * pause recording
     */
    pauseRecording() {
        this.mediaRecorder && this.mediaRecorder.pause();
    }
    /**
     * resume recording
     */
    resumeRecording() {
        this.mediaRecorder && this.mediaRecorder.resume();
    }


    /**
     * provide instance of Navigator native object
     */
    get navigatorObj() {
        try {
            if (navigator) {
                return navigator;
            }
        } catch (error) {
            throw new Error(errorMessage.Navigator);
        }
    }

    /**
     * initiate local stream for local recording #e
     */
    async getUserMediaStream(constraintSetting: any = { video: true, audio: true }) {
        try {
            const constraint = Object.assign({}, constraintSetting);
            if (constraint && typeof constraint.video !== 'boolean' && constraint.video && constraint.video.deviceId) {
                constraint.video.deviceId = {
                    exact: constraint.video.deviceId
                }
            }
            if (constraint && typeof constraint.audio !== 'boolean' && constraint.audio && constraint.audio.deviceId) {
                constraint.audio.deviceId = {
                    exact: constraint.audio.deviceId
                }
            }
            return await this.navigatorObj.mediaDevices.getUserMedia(constraint);
        } catch (error) {
            this.logger.error('getUserMediaStream', error);
            throw error;
        }
    }


    extractUserMediaError(error: any) {
        const res = {
            devicePermission: false,
            videoSource: false,
            audioSource: false
        }
        try {
            if (error && error.message) {
                const { message } = error;
                this.logger.log(`handle device error ${message}`);
                if (message.includes(errorCodeMsg.wrtcVSrc)) {
                    res.videoSource = true;
                }
                else if (message.includes(errorCodeMsg.wrtcASrc)) {
                    res.audioSource = true;
                }
                else if (message.includes("Permission denied")) {
                    res.devicePermission = true;
                }
            }
        } catch (error) {
            this.logger.error(`extractUserMediaError`, error)
        }
        return res;
    }

    handleUserMediaError(error: any, mediaDevice?: MediaDevicesEnum) {
        let deviceName = '';
        try {
            const res = this.extractUserMediaError(error);
            deviceName = res.audioSource ? DEVICES.MIC :
                res.videoSource ? DEVICES.CAM : ''

            if (res.devicePermission && mediaDevice) {
                mediaDevice === MediaDevicesEnum.AudioInput ?
                    DEVICES.MIC :
                    mediaDevice === MediaDevicesEnum.VideoOutput ? DEVICES.CAM : '';
            }

        } catch (error) {
            this.logger.error(`handleUserMediaError`, error);
        }
        this.openPermissionDialog(deviceName);
    }

    /**
     * validate devices existance and show alter
     */
    async devicesPermissionAlert() {
        let checkAudioVideoDeviceObj = await this.checkAudioVideoDevice();
        if (checkAudioVideoDeviceObj.showAlert) {
            this.showDeviceAlert(checkAudioVideoDeviceObj);
            return false;
        }
        return true;
    }

    /**
     * check audio/video device permission 
     */
    async checkAudioVideoDevice() {
        let hasAudioInput = await this.checkMediaPermission(MediaDevicesEnum.AudioInput);
        let hasVideoInput = await this.checkMediaPermission(MediaDevicesEnum.VideoOutput);
        return {
            hasAudioInput: hasAudioInput,
            hasVideoInput: hasVideoInput,
            showAlert: (!hasAudioInput)
        }
        //showAlert: (!hasAudioInput || !hasVideoInput || !isMicCamBtn)
    }


    async askDevicesPermissions(onlyMic = false) {
        const res = {
            success: false,
            error: {
                devicePermission: false,
                videoSource: false,
                audioSource: false
            }
        };
        try {

            const constraint = {
                audio: true,
                video: onlyMic ? false : true
            }
            const resp = await this.getUserMediaStream(constraint);
            res.success = resp ? true : false;
            this.removeStream(resp);
        } catch (error) {
            res.error = this.extractUserMediaError(error);
        }
        return res;
    }



    /**
     * check Media Permission for specific device
     * @param mediaDevice {MediaDevices} enum
     * @param navigator {Navigator} navtive element
     */
    async checkMediaPermission(mediaDevice: MediaDevicesEnum, deviceId?: string, showPopup = true): Promise<boolean> {
        let res: boolean = false;
        let constraint = {}
        if (mediaDevice === MediaDevicesEnum.AudioInput) {
            if (deviceId) {
                Object.assign(constraint, {
                    audio: {
                        deviceId: deviceId
                    }
                })
            }
            else {
                Object.assign(constraint, { audio: true })
            }
        }
        else if (mediaDevice === MediaDevicesEnum.VideoOutput) {
            if (deviceId) {
                Object.assign(constraint, {
                    video: {
                        deviceId: deviceId
                    }
                })
            }
            else {
                Object.assign(constraint, { video: true })
            }
        }
        // let constraint = MediaDevicesEnum.AudioInput === mediaDevice ? { audio: true } : { video: true };
        try {
            let resp = await this.getUserMediaStream(constraint);
            res = resp ? true : false;
            this.removeStream(resp);
        }
        catch (error) {
            if (showPopup) {
                this.handleUserMediaError(error, mediaDevice)
            }
            res = false;
        }
        return res;
    }

    /**
     * show alert if specific device required permission
     * @param checkAudioVideoDeviceObj 
     */
    showDeviceAlert(checkAudioVideoDeviceObj: any) {
        try {
            let devices = checkAudioVideoDeviceObj;
            if (!devices.hasAudioInput || !devices.isMicCamBtnOn) {
                this.openPermissionDialog(DEVICES.MIC);
            }
        } catch (error) {
            this.logger.error('showDeviceAlert', error);
        }

    }


    private permissionPopup = this.getPermissionPopup();


    /**
     * show a pop up when mic permission is not allowed
     */

    openPermissionDialog(device?: string, cb?: (res: any) => void) {
        return this.permissionPopup(device);
    }

    /**
     * get device permission dialog 
     * @returns 
     */
    private getPermissionPopup() {
        let isPopupOpened = false;
        return async (device: string): Promise<any> => {
            try {
                if (isPopupOpened) return;
                isPopupOpened = true;
                const resultPopup = await new Promise((res, rej) => {
                    const dialogRef = this.dialog.open(DevicePermissionDialogComponent, {
                        width: '1300px',
                        data: device
                    });
                    dialogRef.afterClosed().subscribe((result) => {
                        isPopupOpened = false;
                        res(result);
                    });
                })
                return resultPopup;
            } catch (error) {
                this.logger.error('error in get permission popup', error);
                return false;
            }
        }
    }



    /**
     * append stream to video element node.
     * @param videoNode 
     * @param stream 
     */
    appendStream(videoNode: HTMLVideoElement, stream: MediaStream) {
        try {
            if (videoNode) {
                videoNode.srcObject = stream;
                videoNode.onloadedmetadata = function () {
                    videoNode.play();
                }
            }
        } catch (error) {
            this.logger.error('appendStream', error);
            throw error;
        }
    }

    /**
     * Remove stream
     * @param stream 
     */
    removeStream(stream: MediaStream) {
        try {
            if (!stream) return;
            stream.getTracks().forEach(track => track.stop())
        } catch (error) {
            this.logger.error('removeStream', error);
        }
    }

    /**
     * enable audio
     * @param constraint 
     */
    enableAudio(constraint?: any) {
        try {
            if (this.localStream) {
                let audioTrack = this.getAudioTrack(this.localStream);
                if (audioTrack) {
                    audioTrack.enabled = true;
                    if (constraint) {
                        audioTrack.applyConstraints(constraint);
                        this.logger.log('enableAudio => audioTrack ', audioTrack)
                    }
                }
            }
        } catch (error) {
            this.logger.error('enableAudio', error);
        }
    }

    /**
     * disable audio
     */
    disableAudio() {
        try {
            if (this.localStream) {
                let audioTrack = this.getAudioTrack(this.localStream);
                if (audioTrack) {
                    audioTrack.enabled = false;
                    this.logger.log('disableAudio => audioTrack ', audioTrack)
                }
            }
        } catch (error) {
            this.logger.error('disableAudio', error);
        }
    }

    /**
     * enable local recording
     * @param constraint 
     */
    enableVideo(constraint?: any) {
        try {
            if (this.localStream) {
                let videoTrack = this.getVideoTrack(this.localStream);
                if (videoTrack) {
                    videoTrack.enabled = true;
                    if (constraint) {
                        videoTrack.applyConstraints(constraint)
                        this.logger.log('enableVideo => videoTrack ', videoTrack)
                    }
                }
            }
        } catch (error) {
            this.logger.error('enableVideo', error);
        }
    }

    /**
     * disable video
     */
    disableVideo() {
        try {
            if (this.localStream) {
                let videoTrack = this.getVideoTrack(this.localStream);
                if (videoTrack) {
                    videoTrack.enabled = false;
                    this.logger.log('disableVideo => videoTrack ', videoTrack)
                }
            }
        } catch (error) {
            this.logger.error('disableVideo', error);
        }
    }

    /**
     * get video track from stream
     * @param stream 
     */
    getVideoTrack(stream: MediaStream) {
        try {
            if (stream) {
                let videoTracks = stream.getVideoTracks();
                return (videoTracks && videoTracks.length > 0) ? videoTracks[0] : null;
            }
        } catch (error) {
            this.logger.error('getVideoTrack', error);
        }

    }

    /**
     * get audio track from stream 
     * @param stream 
     */
    getAudioTrack(stream: MediaStream) {
        try {
            if (stream) {
                let audioTracks = stream.getAudioTracks();
                return (audioTracks && audioTracks.length > 0) ? audioTracks[0] : null;
            }
        } catch (error) {
            this.logger.error('getAudioTrack', error);
        }
    }

    /**
      * check wheather stream has video track
      * @param stream 
      */
    hasVideoTrack(stream: MediaStream) {
        try {
            if (!stream) return;
            let track = stream.getVideoTracks();
            if (track.length > 0) {
                return true;
            }
            else {
                return false;
            }
        } catch (error) {
            this.logger.error('getVideStreamCapability', error);
            return false;
        }
    }

    /**
     * check wheather stream has audio track
     * @param stream 
     */
    hasAudioTrack(stream: MediaStream) {
        try {
            if (!stream) return;
            let track = stream.getAudioTracks();
            if (track.length > 0) {
                return true;
            }
            else {
                return false;
            }
        } catch (error) {
            this.logger.error('getVideStreamCapability', error);
            return false;
        }
    }

    /**
     * get video stream capability
     * @param stream 
     */
    getVideStreamCapability(stream: MediaStream) {
        try {
            let cap = null;
            if (stream) {
                stream.getVideoTracks().forEach(t => {
                    let videoCap = t.getCapabilities();
                    cap = videoCap;
                })
            }
            return cap;
        } catch (error) {
            this.logger.error('getVideStreamCapability', error);
        }

    }

    /**
     * get video stream for manage preview
     * @param deviceId 
     */
    async getVideoStreamPreview(deviceId?: string) {
        try {
            let constraint = {
                audio: false,
                video: {
                    width: 3840,
                    height: 2160
                }
            }
            if (deviceId) {
                constraint.video['deviceId'] = deviceId
            }
            return await this.getUserMediaStream(constraint);
        } catch (error) {
            throw error;
        }
    }

    /**
     * set and apply setting to local stream for recording
     * @param constraintConfig 
     * @param recQuality 
     * @param recType 
     */
    async setLocalStream(constraintConfig: IManageSessionConstraint, recQuality: number, recType: RecordingTypeEnum, trackEndEvent: (res) => void) {
        try {
            this.removeStream(this.localStream);
            this.localStream = await this.mapStreamWithLive(constraintConfig, recQuality, recType);
            this.setLocalStreamTrackEvent(trackEndEvent);
            this.logger.logStream(`set: this.localStream`, this.localStream);
            //window["localStream"] = this.localStream;
            return true;
        } catch (error) {
            this.logger.error('setLocalStream', error);
            throw error;
        }
    }

    /**
     * set local stream track events
     * @param cb 
     */
    setLocalStreamTrackEvent(cb: (res: any) => void) {
        this.localStream.getTracks().forEach(t => {
            t.onended = cb
        })
    }

    /**
     * map stream with live
     * and apply user defined setting over local stream for local recording
     * @param constraintConfig 
     * @param recQuality 
     * @param recType 
     */
    private async mapStreamWithLive(constraintConfig: IManageSessionConstraint, recQuality: number, recType: RecordingTypeEnum) {
        try {
            const constraint = {
                video: false,
                audio: false,
            }
            if (recType == RecordingTypeEnum.audioOnly) {
                constraint.audio = true;
            } else {
                const deviceId = this.getVideoDeviceId(constraintConfig);
                const hasVideoInput = await this.checkMediaPermission(MediaDevicesEnum.VideoOutput, deviceId);
                constraint.video = hasVideoInput ? await this.getLocalRecordingResolution(constraintConfig.constraint.video, recQuality) : false;
                constraint.audio = { ...constraintConfig.constraint.audio };
            }
            const stream = await this.getUserMediaStream(constraint);
            ////  this.logger.logStream(`new stream`, stream)
            return stream;
        } catch (error) {
            this.handleUserMediaError(error);
        }
    }

    getVideoDeviceId(constraintConfig: IManageSessionConstraint) {
        try {
            return constraintConfig.constraint.video.deviceId
        } catch (error) {
            return ''
        }
    }
    /**
     * get video resolution for local recording
     */
    private async getLocalRecordingResolution(video: any, recQuality: number) {
        try {
            let videoO = Object.assign({}, video),
                width = 1280, height = 720,
                defaultConfig = true, p480Id = 1;

            try {
                if (this.isVideoResolutionDropByLogicalCore) {
                    this.logger.log('logicalProcessors hardwareConcurrency ', this.logicalProcessors);
                    getVideoResoultion().some(r => {
                        if (r.id === p480Id) {
                            width = r.width;
                            height = r.height;
                            return true;
                        }
                    });
                    defaultConfig = false;
                }
            } catch (error) {
                this.logger.error(`hardwareConcurrency logicalProcessors `, error);
            }

            if (this.videoResolution) {
                try {
                    console.log('video-resolution - ', this.videoResolution);
                    let [w, h] = this.videoResolution.split('x');
                    width = Number(w);
                    height = Number(h);
                    console.log('video-resolution - ', width, height);
                    defaultConfig = false;
                    videoO.width = { exact: width };
                    videoO.height = { exact: height };
                } catch (error) {
                    this.logger.error(`videoResolution  `, error);
                }
            }

            if (defaultConfig) {
                getVideoResoultion().some(res => {
                    if (res.id === recQuality) {
                        width = res.width;
                        height = res.height;
                        return true;
                    }
                });
            }
            if (!this.videoResolution) {
                videoO.width = width;
                videoO.height = height;
            }
            videoO.resizeMode = 'none'
            console.log('video resoul', video)
            return videoO;
        } catch (error) {
            this.logger.error(`getLocalRecordingResolution`, error);
            throw error;
        }
    }

    /**
    * remove local stream
    */
    removeLocalStream() {
        try {
            if (this.localStream) {
                this.removeStream(this.localStream);
            }
        } catch (error) {
            this.logger.error('removeLocalStream', error);
        }
    }

    /**
     * get supported mimetype for mediarecorder
     */
    getSupportedMimeType() {
        let mimeType = null;
        try {
            [
                'video/x-matroska;codecs=avc1,pcm',
                'video/x-matroska;codecs=h264,opus',
                'video/webm;codecs=vp9,opus',
                'video/webm;codecs=vp8,opus',
                'video/webm'
            ].some(v => {
                if (MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(v)) {
                    mimeType = v;
                    return true;
                }
            })
            if (!mimeType) {
                throw 'MediaRecorder.isTypeSupported: NO'
            }
            return mimeType;
        } catch (error) {
            this.logger.error('codec is not supported', error)
            throw error;
        }
    }

    /**
     * get video bit rate
     * @param stream 
     */
    getVideoBitRate(stream: MediaStream) {
        try {
            var track = stream.getVideoTracks();
            if (track && track.length > 0) {
                var setting = track[0].getSettings();
                if (setting.height) {
                    let bitrate = setting.height >= 1080 ? 8e6 : setting.height >= 720 ? 6e6 : 25e5;
                    return bitrate;
                }
            }
        } catch (error) {
            this.logger.error('getVideoBitRate', error)
            return 25e5;
        }
    }

    /**
     * check whether browser is in private mode
     */
    async isPrivateWindow() {
        try {
            await this.recIndexDbService.init();
            const url = `${location.origin}/assets/images/media-recorder-2.png`;
            const blobData = await this.toDataURL(url);
            let id = 1e9, part = 1, group = 1;
            const { usageInMib: usedBefore } = await this.commonService.getStorageEstimateInfo();
            await this.recIndexDbService.putFileChunk(id, part, blobData, group);
            const { usageInMib: usedAfterPut } = await this.commonService.getStorageEstimateInfo();
            await this.recIndexDbService.removeItemIDB(id, group)
            const { usageInMib: usedAfter } = await this.commonService.getStorageEstimateInfo();
            if ((usedBefore === usedAfterPut) || (usedBefore !== usedAfter)) {
                return true;
            }
            return false;
        } catch (error) {
            throw error;
        }
    }

    /**
     * get to data url
     * @param url 
     * @param callback 
     */
    toDataURL(url: string): Promise<any> {
        return new Promise((resolve, reject) => {
            var xhr = new XMLHttpRequest();
            xhr.onload = function () {
                var reader = new FileReader();
                reader.onloadend = function () {
                    resolve(reader.result);
                }
                reader.readAsDataURL(xhr.response);
            };
            xhr.onerror = function () {
                reject(xhr.response);
            }
            xhr.open('GET', url);
            xhr.responseType = 'blob';
            xhr.send();
        })
    }

    /**
     * handle eror on local recording service
     */
    handleError(type: LocalWebRtcErrorEnum, method: string, error?: any) {
        try {
            let message = ''
            switch (type) {
                case LocalWebRtcErrorEnum.RequestChunks:
                    message = 'There is some issue with recording locally.'
                    break;
                case LocalWebRtcErrorEnum.PutChunkToDb:
                    message = 'There is some issue while saving the recording part locally'
                    break;
                case LocalWebRtcErrorEnum.DeviceChanged:
                    message = 'Please check you mic or camera other wise local recording won`t work properly.'
                    break;
            }
            message && this.commonService.showErrorAlert(message);
            error['method'] = method;
            this.logger.error(message, error)
        } catch (error) { }
    }

    /**
     * set/register on device change event
     * @param cb 
     */
    setOnDeviceChangeEvent(cb: (event: any) => void) {
        //let lock = false
        this.navigatorObj.mediaDevices.ondevicechange = async (e) => {
            // if (lock) return;
            //lock = true;
            cb(e);
            //await sleep(11e2)
            //lock = false;
        };
    }

    /**
     * print local recording state
     */
    printLocalRecordingState() {
        this.logger.logStream(`localstream`, this.localStream)
    }

    isLocalStreamTracksLive() {
        return this.checkStreamTrackLiveState(this.localStream);
    }

    /**
     * check local stream tracks state
     * @returns 
     */
    checkStreamTrackLiveState(stream: MediaStream) {
        try {
            if (stream) {
                const atrack = this.getAudioTrack(stream);
                const vtrack = this.getVideoTrack(stream);
                const trackStateBool = (state: string) => state !== 'ended'
                const audioState = trackStateBool(atrack.readyState);
                const videoState = trackStateBool(vtrack.readyState);
                return {
                    audioState,
                    videoState
                }
            }
        } catch (error) {

        }
    }

    /**
     * log devices 
     * @param cam 
     * @param mic 
     * @param join 
     * @param isprivate 
     */
    logDevices(cam, mic, join, isprivate) {
        try {
            const { connection, deviceMemory, hardwareConcurrency } = navigator as any;
            let connectionO: any
            try {
                const { effectiveType, downlink, rtt, saveData } = connection;
                connectionO = {
                    effectiveType, downlink, rtt, saveData
                }

            } catch (error) {

            }
            let logO = {
                connectionO,
                deviceMemory,
                hardwareConcurrency,
                isprivate,
                cam,
                mic,
                utc: new Date().toISOString()
            }
            let logStr = this.commonService.stringfyJson(logO);
            this.logger.log(`${join} log devices : `, logStr)
        } catch (error) {
            this.logger.error(`logDevices`, error);
        }
    }

    /**
     * get devices constraint configuration
     * @returns 
     */
    getConstraintConfig(): IManageSessionConstraint {
        try {
            const constraints: IManageSessionConstraint = this.commonService.getLocalStorage(CB_APP_CONSTRAINTS, true);
            if (isObjectValidAndNotEmpty(constraints)) {
                return constraints;
            }
            else {
                throw new Error(`no constraint setting in local stroage`);
            }
        } catch (error) {
            throw error;
        }
    }

    /**
     * get hardware concurrency of the system/device
     */
    get logicalProcessors() {
        return navigator.hardwareConcurrency;
    }

    get isVideoResolutionDropByLogicalCore() {
        try {
            return this.logicalProcessors < 4
        } catch (error) {
            this.logger.error('isVideoResolutionDropLocialCore', error);
            return false;
        }

    }

    getDeviceSetting(stream: MediaStream): DeviceConfigSetting[] {
        const device = []
        if (stream) {
            const tracks = stream.getTracks();
            for (let track of tracks) {
                const setting = track.getSettings();
                setting.deviceId;
                device.push({
                    kind: track.kind,
                    label: track.label,
                    deviceId: setting.deviceId
                })
            }
        }
        return device;
    }

    getLocalStreamSetting() {
        return this.getDeviceSetting(this.localStream);
    }

    async persist() {
        return navigator.storage && navigator.storage.persist &&
            navigator.storage.persist();
    }

    async isStoragePersisted() {
        return navigator.storage && navigator.storage.persisted &&
            navigator.storage.persisted();
    }

    getDevicesInfo() {
        const deviceInfo = {
            mic: {
                name: '',
                echoCancellation: false
            },
            cam: {
                name: '',
                resolution: ''
            }
        }

        try {
            if (!this.localStream) return deviceInfo;
            this.localStream.getTracks().forEach(t => {
                switch (t.kind) {
                    case 'video': {
                        try {
                            deviceInfo.cam.name = t.label;
                            const s = t.getSettings();
                            if (s) {
                                deviceInfo.cam.resolution = `${s.width}x${s.height}`;
                            }
                        } catch (error) { }
                    }
                        break;
                    case 'audio': {
                        try {
                            deviceInfo.mic.name = t.label;
                            const s = t.getSettings();
                            if (s) {
                                deviceInfo.mic.echoCancellation = s.echoCancellation;
                            }
                        } catch (error) { }
                    }
                        break;
                }
            });
            return deviceInfo;
        } catch (error) {
            throw error;
        }
    }

    async getDeviceInfoById(deviceId: string, type: enumeratDevicesTypeEnum) {
        const devices = await this.navigatorObj.mediaDevices.enumerateDevices();
        let info: any;
        devices.some(d => {
            if (d.deviceId === deviceId && type === d.kind) {
                info = d;
                return true;
            }
        })
        //console.log('info of device by id ', info)
        return info;
    }

    getVideoResolution() {
        let res = ''
        this.localStream.getTracks().forEach(t => {
            switch (t.kind) {
                case 'video': {
                    try {
                        const s = t.getSettings();
                        if (s) {
                            res = `${s.width}x${s.height}`;
                        }
                    } catch (error) { }
                }
                    break;
            }
        });
        return res;
    }

    isLocalStreamAdded() {
        return !!this.localStream
    }

}


enum LocalWebrtcEventEnum {
    lowStorage
}


enum LocalWebRtcErrorEnum {
    RequestChunks,
    PutChunkToDb,
    DeviceChanged
}

export enum MediaRecorderStateEnum {
    inactive = 'inactive',
    recording = 'recording',
    paused = 'paused'
}

export interface DeviceConfigSetting {
    kind: string, label: string, deviceId: string
}